import { Alert, Stack, TextField } from '@mui/material';
import type { ColDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { PageContainer } from '@tiles-erp/ui';
import { DataTable } from '../../components/DataTable';
import { useIncentives, type ProductIncentiveItem } from './incentive-api';

/** Current fixed incentive master, limited to products with a positive assigned incentive. */
export function IncentiveProductListPage(): JSX.Element {
  const incentives = useIncentives();
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return (incentives.data ?? []).filter((row) => !term || [
      row.product.sku, row.product.name, row.product.brand.name,
      row.product.category.name, row.product.sizeMm ?? '',
    ].some((value) => value.toLocaleLowerCase().includes(term)));
  }, [incentives.data, search]);
  const columns = useMemo<ColDef<ProductIncentiveItem>[]>(() => [
    { headerName: 'SKU', minWidth: 130, valueGetter: (p) => p.data?.product.sku ?? '' },
    { headerName: 'Product', minWidth: 250, valueGetter: (p) => p.data?.product.name ?? '' },
    { headerName: 'Brand', minWidth: 150, valueGetter: (p) => p.data?.product.brand.name ?? '' },
    { headerName: 'Category', minWidth: 170, valueGetter: (p) => p.data?.product.category.name ?? '' },
    { headerName: 'Size', minWidth: 110, valueGetter: (p) => p.data?.product.sizeMm ?? '' },
    { field: 'amountPerBox', headerName: 'Incentive ₹ / box', minWidth: 160,
      valueFormatter: (p) => Number(p.value ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
  ], []);

  return <PageContainer title="Incentive-assigned products"
    subtitle="Products currently assigned a fixed salesman incentive per sold box.">
    <Stack spacing={1.5}>
      {incentives.error && <Alert severity="error">{incentives.error.message}</Alert>}
      <TextField size="small" label="Search" placeholder="SKU, product, brand, category or size"
        value={search} onChange={(event) => setSearch(event.target.value)} sx={{ maxWidth: 420 }} />
      <DataTable rows={rows} columns={columns} loading={incentives.isFetching}
        exportTitle="Incentive-assigned products" exportFilename="incentive-assigned-products" height={650} />
    </Stack>
  </PageContainer>;
}
